import React from "react";

const fmt = (v) => (Number(v || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const toBR = (iso) => {
  if (!iso) return "";
  const s = typeof iso === "string" && !(iso.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(iso)) ? iso + "Z" : iso;
  return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

// Agrupa itens de combo da venda salva numa linha única "COMBO"
export const groupSaleItems = (items = []) => {
  const out = [];
  const combos = {};
  items.forEach((it) => {
    if (!it.combo_id) {
      out.push({ name: it.product_name || it.name, unit: it.unit, quantity: it.quantity, unit_price: it.unit_price });
      return;
    }
    if (!combos[it.combo_id]) {
      combos[it.combo_id] = { is_combo: true, name: it.combo_name, unit: "combo", quantity: Number(it.combo_qty || 1), total: 0, components: [] };
      out.push(combos[it.combo_id]);
    }
    combos[it.combo_id].total += Number(it.total_price ?? it.quantity * it.unit_price);
    combos[it.combo_id].components.push(`${it.quantity} ${it.unit || ""} ${it.product_name || ""}`.trim());
  });
  return out.map((x) => (x.is_combo ? { ...x, unit_price: x.total / (x.quantity || 1) } : x));
};

export const saleToReceipt = (sale, companyName) => ({
  sale_id: sale.id,
  sale_number: sale.sale_number,
  company_name: companyName || "",
  date: toBR(sale.created_at),
  client: sale.client_name || "Consumidor",
  seller: sale.seller_name || "—",
  price_type: (sale.items || [])[0]?.price_type || "",
  items: groupSaleItems(sale.items || []),
  subtotal: sale.subtotal,
  discount: sale.discount,
  freight: sale.freight || 0,
  total: sale.total,
  payment_method: sale.payment_method,
  is_pix_aprazo: sale.is_pix_aprazo,
  card_installments: sale.credit_card_installments || 1,
  aprazo_count: sale.installments_count || 1,
  amount_received: sale.amount_received,
  change: sale.change_amount,
  pix_received: sale.pix_amount_received,
  pix_change: sale.payment_method === "pix" ? sale.change_amount : 0,
  pix_attachment_url: sale.pix_attachment_url,
  nfce_status: sale.nfce_status,
  nfce_chave: sale.nfce_chave,
});

// Imprime em janela nova via DOM API (sem document.write)
export const printReceiptNode = (node, title) => {
  if (!node) return false;
  const w = window.open("", "_blank", "width=380,height=600");
  if (!w) return false;
  const doc = w.document;
  doc.title = title;
  const style = doc.createElement("style");
  style.textContent = `body{font-family: 'Courier New', monospace; font-size: 12px; padding: 8px; width: 80mm; margin:0;}
    .center{text-align:center} .bold{font-weight:bold}
    hr{border:none; border-top:1px dashed #000; margin:6px 0;}
    table{width:100%; border-collapse: collapse;} .right{text-align:right} .big{font-size: 14px;}`;
  doc.head.appendChild(style);
  doc.body.innerHTML = node.innerHTML;
  w.focus();
  setTimeout(() => { w.print(); }, 250);
  return true;
};

const payLabel = (r) => {
  if (r.is_pix_aprazo) return "PIX A PRAZO 4d";
  return ({
    dinheiro: "DINHEIRO", pix: "PIX", cartao: "CARTÃO", cartao_debito: "DÉBITO",
    cartao_credito: `CRÉDITO ${r.card_installments || 1}x`,
    aprazo: `A PRAZO ${r.aprazo_count || 1}x`,
  })[r.payment_method] || r.payment_method || "";
};

const ItemsTable = ({ items }) => (
  <table>
    <tbody>
      {items.map((it, i) => (
        <tr key={`${it.name}-${i}`}>
          <td colSpan={2}>
            <div>{it.is_combo ? `COMBO ${it.name}` : it.name}</div>
            <div style={{ fontSize: 11 }}>{it.quantity} {it.unit} x {fmt(it.unit_price)}</div>
            {it.is_combo && it.components?.length > 0 && <div style={{ fontSize: 10 }}>({it.components.join(" + ")})</div>}
          </td>
          <td className="right bold">{fmt(it.quantity * it.unit_price)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PaymentInfo = ({ r }) => (
  <>
    <div>Pagamento: <span className="bold">{payLabel(r)}</span></div>
    {r.payment_method === "dinheiro" && r.amount_received != null && (
      <>
        <div>Recebido: {fmt(r.amount_received)}</div>
        <div>Troco: <span className="bold">{fmt(r.change || 0)}</span></div>
      </>
    )}
    {r.payment_method === "pix" && r.pix_received && (
      <>
        <div>PIX recebido: {fmt(r.pix_received)}</div>
        {r.pix_change > 0 && <div>Troco em dinheiro: <span className="bold">{fmt(r.pix_change)}</span></div>}
        {r.pix_attachment_url && <div style={{ fontSize: 10 }}>✓ Comprovante anexado</div>}
      </>
    )}
    {r.payment_method === "aprazo" && r.first_due_date && <div style={{ fontSize: 11 }}>1ª parcela: {r.first_due_date}</div>}
    {r.delivery && <div style={{ fontSize: 11 }} data-testid="receipt-delivery">Entrega: {r.delivery}</div>}
    {r.nfce_status === "autorizado" && (
      <>
        <hr />
        <div className="bold center">NFC-e AUTORIZADA</div>
        {r.nfce_chave && <div style={{ fontSize: 9, wordBreak: "break-all" }}>Chave: {r.nfce_chave}</div>}
      </>
    )}
    {r.nfce_status && r.nfce_status !== "autorizado" && (
      <>
        <hr />
        <div className="bold center">NFC-e: {r.nfce_status}</div>
        {r.nfce_error && <div style={{ fontSize: 10 }}>{r.nfce_error}</div>}
      </>
    )}
  </>
);

export const SaleReceiptContent = ({ receipt, duplicate = false }) => {
  if (!receipt) return null;
  return (
    <div data-testid="sale-receipt-content" className="sale-receipt font-mono text-xs space-y-0.5">
      <style>{`.sale-receipt .center{text-align:center}.sale-receipt .bold{font-weight:bold}.sale-receipt .right{text-align:right}.sale-receipt .big{font-size:14px}.sale-receipt hr{border:none;border-top:1px dashed #000;margin:6px 0}.sale-receipt table{width:100%;border-collapse:collapse}`}</style>
      <div className="center bold big">{receipt.company_name}</div>
      <div className="center">CUPOM NÃO FISCAL</div>
      {duplicate && <div className="center bold" data-testid="receipt-duplicate-label">*** 2ª VIA ***</div>}
      <hr />
      <div>Venda: <span className="bold">#{String(receipt.sale_number || "").padStart(4, "0")}</span></div>
      <div>Data: {receipt.date}</div>
      <div>Cliente: {receipt.client}</div>
      <div>Vendedor: {receipt.seller}</div>
      <div>Tabela: {(receipt.price_type || "").toUpperCase()}</div>
      <hr />
      <ItemsTable items={receipt.items || []} />
      <hr />
      <table>
        <tbody>
          <tr><td>Subtotal</td><td className="right">{fmt(receipt.subtotal || 0)}</td></tr>
          {(receipt.discount || 0) > 0 && <tr><td>Desconto</td><td className="right">-{fmt(receipt.discount)}</td></tr>}
          {(receipt.freight || 0) > 0 && <tr data-testid="receipt-freight"><td>Frete</td><td className="right">+{fmt(receipt.freight)}</td></tr>}
          <tr><td className="bold big">TOTAL</td><td className="right bold big">{fmt(receipt.total || 0)}</td></tr>
        </tbody>
      </table>
      <hr />
      <PaymentInfo r={receipt} />
      <hr />
      {duplicate && <div className="center" style={{ fontSize: 10 }}>2ª via emitida em {new Date().toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}</div>}
      <div className="center" style={{ fontSize: 11 }}>Obrigado pela preferência!</div>
    </div>
  );
};
